import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import api, { BRAND } from "@/lib/appApi";
import { useAuth } from "@/context/AuthContext";
import AgeGate from "@/components/AgeGate";
import { Video, MessageSquare, Shuffle, ShieldCheck, Crown, ArrowRight } from "lucide-react";

export default function Landing() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [gatePassed, setGatePassed] = useState(false);
  const [maintenance, setMaintenance] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("age_confirmed") === "true") setGatePassed(true);
    api.get("/config").then((r) => setMaintenance(!!r.data.maintenance_mode)).catch(() => {});
  }, []);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handlePrimary = () => {
    if (user) navigate("/app");
    else login();
  };

  const features = [
    { icon: Video, title: "Live Video & Audio", desc: "Instant face-to-face chat with strangers worldwide over crystal-clear WebRTC." },
    { icon: Shuffle, title: "Skip Anytime", desc: "One tap on Next drops you into a brand-new conversation. No waiting around." },
    { icon: MessageSquare, title: "Text Alongside Video", desc: "Type in a sleek side panel while you talk. Resize it however you like." },
    { icon: Crown, title: "Gender Filters", desc: "Premium members choose exactly who they meet — filter by gender preference." },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {!gatePassed && <AgeGate onPass={() => setGatePassed(true)} onExit={() => (window.location.href = "https://www.google.com")} />}

      {maintenance && (
        <div data-testid="landing-maintenance-banner" className="relative z-20 w-full bg-amber-500/90 text-black text-center text-sm py-2 px-4 font-semibold">
          🔧 Random Cam Chat is currently under maintenance. Some features may be unavailable — please check back soon.
        </div>
      )}

      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full opacity-20 blur-[120px] pointer-events-none"
           style={{ background: "radial-gradient(circle, #a855f7 0%, #e11d48 60%, transparent 70%)" }} />

      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <img src={BRAND.logo} alt="logo" className="w-10 h-10 rounded-full" />
          <span className="font-heading font-extrabold text-lg tracking-tight">Random Cam <span className="brand-gradient-text">Chat</span></span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => navigate("/pricing")} data-testid="nav-pricing-btn" className="text-muted-foreground hover:text-foreground">Pricing</Button>
          <Button onClick={handlePrimary} data-testid="nav-login-btn" className="brand-gradient-bg text-white border-0 rounded-full px-5 transition-transform hover:scale-105">
            {user ? "Enter" : "Sign in"}
          </Button>
        </div>
      </header>

      <main className="relative z-10 px-6 md:px-12">
        <section className="max-w-4xl mx-auto text-center pt-16 md:pt-24 pb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-border glass text-sm text-muted-foreground mb-8">
            <ShieldCheck className="w-4 h-4 text-primary" /> 18+ verified adult video community
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight">
            Meet new people. <br /><span className="brand-gradient-text">One video chat at a time.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Random Cam Chat pairs you with real people on camera. Turn on your cam, hit start, and press Next whenever you want someone new.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={handlePrimary} disabled={loading} data-testid="hero-start-btn"
              className="brand-gradient-bg text-white border-0 rounded-full h-12 px-8 text-base font-semibold transition-transform hover:scale-105">
              {user ? "Start chatting" : "Continue with Google"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <Button variant="outline" onClick={() => navigate("/pricing")} data-testid="hero-pricing-btn"
              className="rounded-full h-12 px-8 text-base border-border bg-secondary hover:bg-accent">
              View plans
            </Button>
          </div>
          <p className="mt-4 text-xs text-muted-foreground">Free to chat · Premium unlocks gender filters</p>
        </section>

        <section className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 pb-24">
          {features.map((f, i) => (
            <div key={i} className="glass border border-border rounded-2xl p-6 flex gap-4 transition-colors hover:border-primary/40">
              <div className="shrink-0 w-11 h-11 rounded-xl brand-gradient-bg flex items-center justify-center">
                <f.icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">{f.title}</h3>
                <p className="text-sm text-muted-foreground mt-1">{f.desc}</p>
              </div>
            </div>
          ))}
        </section>
      </main>

      <footer className="relative z-10 border-t border-border py-6 text-center text-xs text-muted-foreground">
        © {new Date().getFullYear()} Random Cam Chat · Adults only (18+/21+) ·{" "}
        <button onClick={() => navigate("/privacy")} data-testid="footer-privacy" className="underline hover:text-foreground">Privacy Policy</button>
        {" "}·{" "}
        <button onClick={() => navigate("/terms")} data-testid="footer-terms" className="underline hover:text-foreground">Terms &amp; Conditions</button>
      </footer>
    </div>
  );
}
